import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type MessageProps } from '@post-office/placement-common/utils/message-renderer';
import { type renderer } from '../__generated__/client/render';
import { JiraSideNavVariant } from '@post-office/message-template--rovo-extension-install-cta-left-nav/constants';

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

// Experiment cohort can be control, not-enrolled, or specific variation names
type ExperimentCohort = string;

type MessageExperimentConfiguration = Record<
	string,
	{
		experimentKey: string;
		/**
		 * List of specific variation cohort names that should show this message.
		 * Only users in these cohorts will see the message.
		 * Uses shared constants from message template to ensure consistency.
		 */
		allowedCohorts: readonly string[];
		/**
		 * Optional AA experiment key to fire exposure for alongside the main experiment.
		 */
		aaExperimentKey?: string;
		/**
		 * Optional Statsig parameter name to read from the experiment.
		 * Defaults to 'cohort' if not specified.
		 */
		statsigParameterName?: string;
	}
>;

export type Message = MessageProps<typeof renderer>;

/**
 * Checks if the cohort is in the allowed list.
 * Note: This is case-sensitive - cohort names must match exactly as configured in the experiment.
 */
const isAllowedCohort = (cohort: ExperimentCohort, allowedCohorts: readonly string[]): boolean => {
	return allowedCohorts.includes(cohort);
};

export const getMessage = (
	initialMessages: { messageTemplateId: string; messageInstanceId: string }[],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
	const filteredMessages = ((initialMessages || []) as Message[]).filter(
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
		(dataMessage) => !excludedMessages.has(dataMessage.messageTemplateId),
	);

	const messageExperimentConfigs: MessageExperimentConfiguration = {
		'jira-twcg-190-recommend-retrial-recent-trialing-customers': {
			experimentKey: 'twcg-190-recommend-retrial-for-recent-customers',
			allowedCohorts: ['variation'],
		},
		'jira-twcg-810-trial-ad': {
			experimentKey: 'twcg-810-trial-ad',
			allowedCohorts: ['variation'],
			aaExperimentKey: 'twcg-810-trial-ad-aa',
		},
		'rovo-extension-install-cta-left-nav': {
			experimentKey: 'left_nav_rovo_extension_jira_cta_rovo_extension',
			allowedCohorts: [
				JiraSideNavVariant.EXISTING,
				JiraSideNavVariant.NEW,
				JiraSideNavVariant.ROVO,
			],
		},
	};

	for (const message of filteredMessages) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		const messageId = message.messageTemplateId;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		const experimentConfig = messageExperimentConfigs[messageId];

		if (!experimentConfig) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return message;
		}

		const cohort = FeatureGates.getExperimentValue<ExperimentCohort>(
			experimentConfig.experimentKey,
			experimentConfig.statsigParameterName ?? 'cohort',
			'not-enrolled',
			{
				fireExperimentExposure: true,
			},
		);

		if (experimentConfig.aaExperimentKey) {
			FeatureGates.getExperimentValue<ExperimentCohort>(
				experimentConfig.aaExperimentKey,
				'cohort',
				'not-enrolled',
				{
					fireExperimentExposure: true,
				},
			);
		}

		// Show message only if user is in one of the allowed variation cohorts
		if (isAllowedCohort(cohort, experimentConfig.allowedCohorts)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return message;
		}
	}

	return undefined;
};
